'use client';

import { usePaginatedQuery } from 'convex/react';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { InboxPriorityEntry } from '@/convex/organizations/threads/inbox/queries.helpers';
import type { InboxThreadRow } from '@/convex/organizations/threads/inbox/thread_rows.helpers';
import { api, useQuery } from '@/lib/convex';

export const INBOX_ACTIVITY_PAGE_SIZE = 20;

// These queries project rich rows without return validators, so the
// generated client types are loose; the server-side types are the contract.
export type { InboxPriorityEntry } from '@/convex/organizations/threads/inbox/queries.helpers';
export type { InboxThreadRow } from '@/convex/organizations/threads/inbox/thread_rows.helpers';

export type SidebarInboxData = {
  priority: InboxPriorityEntry[] | undefined;
  activity: InboxThreadRow[];
  followed: InboxThreadRow[] | undefined;
  activityStatus:
    | 'LoadingFirstPage'
    | 'CanLoadMore'
    | 'LoadingMore'
    | 'Exhausted';
  loadMoreActivity(): void;
};

const SidebarInboxDataContext = createContext<SidebarInboxData | null>(null);

/**
 * Lives above the sidebar (which remounts on every navigation) so loaded
 * pages and subscriptions survive route changes. Queries run only while the
 * inbox is shown.
 */
export function SidebarInboxDataProvider({
  orgSlug,
  now,
  enabled,
  children,
}: {
  orgSlug?: string;
  /** Minute-rounded clock for expiry checks (queries cannot read time). */
  now: number;
  enabled: boolean;
  children: ReactNode;
}) {
  const args = enabled && orgSlug ? { orgSlug } : 'skip';
  const priority = useQuery(
    api.organizations.threads.sidebar_status.listInboxPriority,
    enabled && orgSlug ? { orgSlug, now } : 'skip'
  );
  const followed = useQuery(
    api.organizations.threads.sidebar_status.listInboxFollowedThreads,
    args
  );
  const activity = usePaginatedQuery(
    api.organizations.threads.sidebar_status.listInboxActivityPaginated,
    args,
    { initialNumItems: INBOX_ACTIVITY_PAGE_SIZE }
  );
  const loadMore = activity.loadMore;
  const value = useMemo<SidebarInboxData>(
    () => ({
      priority: priority.data as InboxPriorityEntry[] | undefined,
      followed: followed.data as InboxThreadRow[] | undefined,
      activity: activity.results as InboxThreadRow[],
      activityStatus: activity.status,
      loadMoreActivity: () => loadMore(INBOX_ACTIVITY_PAGE_SIZE),
    }),
    [activity.results, activity.status, followed.data, loadMore, priority.data]
  );
  return (
    <SidebarInboxDataContext.Provider value={value}>
      {children}
    </SidebarInboxDataContext.Provider>
  );
}

export function useSidebarInboxData(): SidebarInboxData | null {
  return useContext(SidebarInboxDataContext);
}
